import { useQuery } from '@tanstack/preact-query';
import { useContext } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import { buildStopDisruptionLink } from '../components/base/DisruptionAlert/disruptionLinks';
import { useTenantWebsiteBaseHref } from '../config/TenantContext';
import type {
  DisruptionLink,
  Stop,
  StopDetail,
  StopDepartures,
  Visit,
} from '../arcticapi/types';

interface DepartureItem extends Visit {
  id: string;
}

export interface StopDeparturesDetailsState {
  departuresLoading: boolean;
  departuresError: Error | null;
  departuresStop: StopDetail | Stop | null;
  departuresStopName: string | null;
  departuresDisruption: DisruptionLink | null;
  departuresStreetViewLink: string | null;
  departuresLastUpdatedAt: number | null;
  departures: DepartureItem[];
  refreshDepartures: () => void;
}

interface UseStopDeparturesDetailsOptions {
  selectedStopAtcoCode?: string | null;
  departuresHrefOverride?: string | null;
  fallbackStop?: Stop | StopDetail | null;
  fallbackStopName?: string | null;
  autoRefreshEnabled?: boolean;
}

/**
 * Details-data controller for stop departures.
 *
 * Given either a selected stop id or a direct visits href, this hook fetches
 * stop metadata, disruptions, and departure visits. It returns raw data state
 * for the details UI and avoids UI-only formatting such as display strings.
 */
export function useStopDeparturesDetails(
  options: UseStopDeparturesDetailsOptions = {},
): StopDeparturesDetailsState {
  const client = useContext(ApiClientContext);
  const tenantWebsiteBaseHref = useTenantWebsiteBaseHref();
  const selectedStopAtcoCode = options.selectedStopAtcoCode?.trim() || null;
  const autoRefreshEnabled = options.autoRefreshEnabled ?? false;

  const stopDetailsQuery = useQuery<StopDetail, Error>({
    queryKey: ['stop-details', selectedStopAtcoCode],
    enabled: Boolean(client) && selectedStopAtcoCode !== null,
    staleTime: 0,
    gcTime: 0,
    queryFn: ({ signal }) => {
      if (!client || !selectedStopAtcoCode) {
        throw new Error('Stop details request is missing stop information.');
      }

      return client.getStop(selectedStopAtcoCode, { signal });
    },
  });

  const stopVisitsHref =
    options.departuresHrefOverride ??
    stopDetailsQuery.data?.links?.visits ??
    options.fallbackStop?.links?.visits ??
    null;
  const stopAlertsHref =
    stopDetailsQuery.data?.links?.alerts ??
    options.fallbackStop?.links?.alerts ??
    null;

  const stopDisruptionsQuery = useQuery({
    queryKey: ['stop-disruptions', stopAlertsHref],
    enabled: Boolean(client) && stopAlertsHref !== null,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      if (!client || !stopAlertsHref) {
        throw new Error(
          'Stop disruptions request is missing stop information.',
        );
      }

      return client.getDisruptions(stopAlertsHref, { signal });
    },
  });

  const departuresQuery = useQuery<StopDepartures, Error>({
    queryKey: ['stop-departures', stopVisitsHref],
    enabled: Boolean(client) && stopVisitsHref !== null,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: autoRefreshEnabled ? 60_000 : false,
    queryFn: ({ signal }) => {
      if (!client || !stopVisitsHref) {
        throw new Error('Departures request is missing stop information.');
      }

      return client.getStopDepartures(stopVisitsHref, { signal });
    },
  });

  const refreshDepartures = () => {
    departuresQuery.refetch();
  };

  const departuresStop =
    stopDetailsQuery.data ??
    options.fallbackStop ??
    departuresQuery.data?.stop ??
    null;

  return {
    departuresLoading:
      Boolean(client) &&
      (selectedStopAtcoCode !== null || stopVisitsHref !== null) &&
      (stopDetailsQuery.isPending || departuresQuery.isPending),
    departuresError: stopDetailsQuery.error ?? departuresQuery.error,
    departuresStop,
    departuresStopName:
      departuresStop?.commonName ??
      departuresQuery.data?.stop?.commonName ??
      options.fallbackStopName ??
      null,
    departuresDisruption: buildStopDisruptionLink(
      tenantWebsiteBaseHref,
      departuresStop?.atcoCode ?? departuresQuery.data?.stop?.atcoCode ?? null,
      stopDisruptionsQuery.data?.length ?? 0,
    ),
    departuresStreetViewLink:
      stopDetailsQuery.data?.streetViewLinks?.[0] ??
      departuresQuery.data?.streetViewLinks?.[0] ??
      null,
    departuresLastUpdatedAt:
      departuresQuery.dataUpdatedAt > 0 ? departuresQuery.dataUpdatedAt : null,
    departures: (departuresQuery.data?.visits ?? []).map((visit, index) => ({
      ...visit,
      id: `${visit.destinationName}-${visit.line?.name ?? 'line'}-${index}`,
    })),
    refreshDepartures,
  };
}
